<template>
  <v-row class="mb-4" align="center">

    <v-col cols="12" sm="3">
      <v-autocomplete variant="outlined" density="compact" label="Form" placeholder="All" :items="formOptions"
        v-model="selectedForms" item-title="label" item-value="value" multiple return-object :menu-props="formMenuProps"
        @update:model-value="emitDraftChange">
        <template #selection="{ item, index }">
          <template v-if="isAllSelected">
            <span v-if="index === 0" class="text-body-2">
              All
            </span>
          </template>

          <template v-else>
            <!-- Show first 2 chips -->
            <v-chip v-if="index < 2" size="small" variant="tonal" closable class="ma-1"
              @click:close="removeForm(item.raw)">
              {{ item.raw?.label }}
            </v-chip>
            <span v-else-if="index === 2 && extraSelectedCount > 0" class="text-caption text-grey-darken-3 ml-1">
              (+{{ extraSelectedCount }} others)
            </span>
          </template>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col cols="12" sm="3">
      <v-autocomplete variant="outlined" density="compact" label="Period" :items="restrictedPeriodOptions"
        v-model="selectedPeriod" item-title="label" item-value="value" return-object
        @update:model-value="onPeriodChange" />
    </v-col>
    <v-col cols="12" sm="3">
      <v-autocomplete v-if="compareOptions.length" variant="outlined" density="compact" label="Compare With"
        :items="compareOptions" v-model="selectedCompare" item-title="label" item-value="value" return-object
        @update:model-value="onCompareChange" />
    </v-col>
    <v-col cols="12" sm="auto" class="d-flex align-center justify-end">
      <v-btn-group variant="outlined" divided density="compact" class="dashboard-filter-toggle rounded-0 mt-n10">
        <v-btn class="text-none rounded-s-lg" color="brand-color" prepend-icon="mdi-filter-check" rounded="0"
          :disabled="isLoading" :loading="isLoading" @click="applyFilters">
          {{ isLoading ? 'Applying...' : 'Apply' }}
        </v-btn>

        <v-btn class="text-none rounded-e-lg" color="accent-color" prepend-icon="mdi-refresh" rounded="0"
          :disabled="isLoading" @click="refreshFilters">
          Refresh
        </v-btn>
      </v-btn-group>
    </v-col>
  </v-row>
</template>

<script>
import moment from 'moment'
import { PERIOD_OPTIONS, TIME_PERIODS, COMPARE_PERIODS } from '@/store/time-periods'
import { mapGetters } from 'vuex'
import { getFormsByUserId } from '@/api/form'

const FORM_FILTER = {
  ALL_VALUE: 'all',
  ALL_LABEL: 'All',
  MAX_VISIBLE_CHIPS: 2
}

const NONE_COMPARE_OPTION = Object.freeze({ label: '- None -', value: '' })
const FORM_MENU_PROPS = Object.freeze({ closeOnContentClick: false, maxHeight: 180 })
const DATE_FORMAT = 'YYYY-MM-DD'

export default {
  name: 'DashboardFilters',

  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['draft-change', 'apply', 'refresh'],

  data() {
    const allFormOption = { label: FORM_FILTER.ALL_LABEL, value: FORM_FILTER.ALL_VALUE }
    return {
      formMenuProps: FORM_MENU_PROPS,
      periodOptions: PERIOD_OPTIONS,
      selectedPeriod: PERIOD_OPTIONS.find(p => p.value === TIME_PERIODS.WEEK_TO_DATE) || PERIOD_OPTIONS[0],
      selectedCompare: { ...NONE_COMPARE_OPTION },
      formOptions: [
        allFormOption
      ],
      selectedForms: [allFormOption],
      formsLoaded: false
    }
  },

  computed: {
    ...mapGetters(['user']),

    restrictedPeriodOptions() {
      return this.periodOptions || []
    },

    currentPeriodObj() {
      return this.restrictedPeriodOptions.find(p => p.value === this.selectedPeriod?.value) || this.selectedPeriod
    },

    compareOptions() {
      const base = this.currentPeriodObj?.compare || []
      const filtered = base.filter(c => c?.value !== COMPARE_PERIODS.CUSTOM)
      return [{ ...NONE_COMPARE_OPTION }, ...filtered]
    },

    isAllSelected() {
      return (this.selectedForms || []).some(f => f?.value === FORM_FILTER.ALL_VALUE)
    },

    extraSelectedCount() {
      const count = (this.selectedForms || []).length
      return count > FORM_FILTER.MAX_VISIBLE_CHIPS ? (count - FORM_FILTER.MAX_VISIBLE_CHIPS) : 0
    }
  },

  watch: {
    user: {
      immediate: true,
      handler(user) {
        if (user?.id) this.loadForms()
      }
    },

    selectedForms: {
      handler(newVal) {
        const next = Array.isArray(newVal) ? newVal : []
        if (next.length === 0) {
          this.selectedForms = [{ label: FORM_FILTER.ALL_LABEL, value: FORM_FILTER.ALL_VALUE }]
          return
        }

        const { unique, hasAll, hasOthers } = this.deduplicateForms(next)
        if (unique.length !== next.length) {
          this.selectedForms = unique
          return
        }

        if (!hasAll || !hasOthers) return

        // If user selects specific forms along with "All", remove "All"
        const withoutAll = unique.filter(f => f?.value !== FORM_FILTER.ALL_VALUE)
        this.selectedForms = withoutAll.length ? withoutAll : [{ label: FORM_FILTER.ALL_LABEL, value: FORM_FILTER.ALL_VALUE }]
      },
      deep: true
    }
  },

  mounted() {
    const hasValidPeriod = this.restrictedPeriodOptions.some(p => p.value === this.selectedPeriod?.value)
    if (!hasValidPeriod) {
      this.selectedPeriod = this.restrictedPeriodOptions.find(p => p.value === TIME_PERIODS.WEEK_TO_DATE) || this.restrictedPeriodOptions[0]
    }

    const hasValidCompare = this.compareOptions.some(c => c.value === this.selectedCompare?.value)
    if (!hasValidCompare) {
      this.selectedCompare = this.compareOptions[0]
    }

    this.emitDraftChange()
  },

  methods: {
    getAllFormOption() {
      return { label: FORM_FILTER.ALL_LABEL, value: FORM_FILTER.ALL_VALUE }
    },

    deduplicateForms(forms) {
      const seen = new Set()
      const unique = []
      let hasAll = false
      let hasOthers = false

      for (const v of forms) {
        const key = v?.value
        if (key === undefined || key === null) continue
        if (seen.has(key)) continue

        seen.add(key)
        unique.push(v)

        if (key === FORM_FILTER.ALL_VALUE) {
          hasAll = true
        } else {
          hasOthers = true
        }
      }

      return { unique, hasAll, hasOthers }
    },

    normalizedFormSelection() {
      const values = (this.selectedForms || [])
        .map(s => s?.value)
        .filter(v => v !== undefined && v !== null)

      if (values.length === 0) return FORM_FILTER.ALL_VALUE
      if (values.includes(FORM_FILTER.ALL_VALUE)) return FORM_FILTER.ALL_VALUE
      return values
    },

    buildFilterPayload() {
      return {
        period: this.selectedPeriod?.value,
        compare: this.selectedCompare?.value,
        custom_start_date: null,
        custom_end_date: null,
        form: this.normalizedFormSelection()
      }
    },

    getPeriodDates(periodValue) {
      const now = moment()
      let start, end

      switch (periodValue) {
        case TIME_PERIODS.TODAY:
          start = now.clone().startOf('day')
          end = now.clone().endOf('day')
          break
        case TIME_PERIODS.YESTERDAY:
          start = now.clone().subtract(1, 'day').startOf('day')
          end = now.clone().subtract(1, 'day').endOf('day')
          break
        case TIME_PERIODS.WEEK_TO_DATE:
          start = now.clone().startOf('week')
          end = now.clone()
          break
        case TIME_PERIODS.LAST_WEEK:
          start = now.clone().subtract(1, 'week').startOf('week')
          end = now.clone().subtract(1, 'week').endOf('week')
          break
        case TIME_PERIODS.MONTH_TO_DATE:
          start = now.clone().startOf('month')
          end = now.clone()
          break
        case TIME_PERIODS.LAST_MONTH:
          start = now.clone().subtract(1, 'month').startOf('month')
          end = now.clone().subtract(1, 'month').endOf('month')
          break
        case TIME_PERIODS.LAST_3_MONTHS:
          start = now.clone().subtract(3, 'months').startOf('day')
          end = now.clone().endOf('day')
          break
        case TIME_PERIODS.LAST_6_MONTHS:
          start = now.clone().subtract(6, 'months').startOf('day')
          end = now.clone().endOf('day')
          break
        default:
          start = now.clone().startOf('month')
          end = now.clone()
      }

      return { start, end }
    },

    getComparePeriodDates(compareValue, baseStart, baseEnd) {
      if (!compareValue) return null
      if (compareValue === COMPARE_PERIODS.PREVIOUS_YEAR) {
        return {
          start: baseStart.clone().subtract(1, 'year'),
          end: baseEnd.clone().subtract(1, 'year')
        }
      }
      const diffDays = baseEnd.diff(baseStart, 'days') + 1

      const start = baseStart.clone().subtract(diffDays, 'days')
      const end = baseStart.clone().subtract(1, 'days')

      return { start, end }
    },

    buildPayload(userId, filters = null) {
      const filterData = filters || this.buildFilterPayload()
      const { period, compare, form } = filterData

      const basePeriod = this.getPeriodDates(period)
      const comparePeriod = this.getComparePeriodDates(compare, basePeriod.start, basePeriod.end)

      const periodStartDate = basePeriod.start.format(DATE_FORMAT)
      const periodEndDate = basePeriod.end.format(DATE_FORMAT)

      const payload = {
        user_id: userId,
        period: {
          value: period,
          start_date: periodStartDate,
          end_date: periodEndDate
        },
        forms: {
          ids: form === FORM_FILTER.ALL_VALUE ? [] : (Array.isArray(form) ? form : [form])
        }
      }

      if (comparePeriod) {
        payload.compare_period = {
          value: compare,
          start_date: comparePeriod.start.format(DATE_FORMAT),
          end_date: comparePeriod.end.format(DATE_FORMAT)
        }
      }

      return payload
    },

    async loadForms() {
      if (this.formsLoaded) return
      if (!this.user?.id) return

      try {
        const res = await getFormsByUserId({ user_id: this.user.id })
        const forms = res?.data?.data || res?.data || []

        const byId = new Map()
        ;(Array.isArray(forms) ? forms : []).forEach(f => {
          const id = f?.id ?? f?.form_id ?? f?.formId
          const title = f?.title ?? f?.form_title ?? f?.name ?? f?.formName
          if (id === undefined || id === null) return
          if (!byId.has(id)) {
            byId.set(id, { label: title || `Form ${id}`, value: id })
          }
        })
        const mapped = Array.from(byId.values())

        const allOption = this.getAllFormOption()
        this.formOptions = [allOption, ...mapped]

        const currentValues = (this.selectedForms || []).map(s => s?.value)
        const nextSelected = this.formOptions.filter(o => currentValues.includes(o.value))
        this.selectedForms = nextSelected.length ? nextSelected : [allOption]

        this.formsLoaded = true
      } catch (e) {
        console.error('[DashboardFilters] getFormsByUserId failed', e)
      }
    },

    removeForm(item) {
      const idx = this.selectedForms.findIndex(f => f?.value === item?.value)
      if (idx !== -1) {
        this.selectedForms.splice(idx, 1)
      }
      if (this.selectedForms.length === 0) {
        this.selectedForms = [this.getAllFormOption()]
      }
    },

    onPeriodChange() {
      const current = this.selectedCompare?.value
      if (!this.compareOptions.some(c => c.value === current)) {
        this.selectedCompare = this.compareOptions[0]
      }
      this.emitDraftChange()
    },

    onCompareChange() {
      this.emitDraftChange()
    },

    refreshFilters() {
      this.$emit('refresh')
    },

    emitDraftChange() {
      this.$emit('draft-change', this.buildFilterPayload())
    },

    applyFilters() {
      this.$emit('apply', this.buildFilterPayload())
    }
  }
}
</script>
<style scoped>
:deep(.dashboard-filter-toggle .v-btn:hover) {
  transform: none !important;
  box-shadow: none !important;
  background-color: rgba(var(--v-theme-brand-color), 0.22) !important;
}
</style>